import { useEffect } from 'react';
import {
  QueryCache,
  QueryClient,
  QueryKey,
  QueryObserver,
  focusManager,
  useQueryClient
} from 'react-query';

const keyHash = (queryKey: QueryKey): string => JSON.stringify(queryKey);

/**
 * One poll per query key, however many components read it.
 *
 * react-query 3 arms `refetchInterval` PER OBSERVER, and a tick fetches
 * unconditionally, so N components mounted at different moments (a drawer, a
 * row scrolled into view, a control that renders once its group resolves)
 * produce N staggered requests per interval against the same endpoint. Hooks
 * that use this instead of `refetchInterval` share a single timer per key,
 * running at the FASTEST interval any mounted subscriber asks for — so the
 * freshness bound of every screen is exactly what it was with its own timer.
 *
 * Like react-query's own interval, a tick is skipped while the window is not
 * focused unless some subscriber passes `inBackground`.
 */

interface Subscription {
  intervalMs: number;
  inBackground: boolean;
}

interface Poller {
  queryKey: QueryKey;
  subs: Set<Subscription>;
  timer?: ReturnType<typeof setInterval>;
  intervalMs?: number;
}

const pollersByClient = new WeakMap<QueryClient, Map<string, Poller>>();

function pollersOf(client: QueryClient): Map<string, Poller> {
  let pollers = pollersByClient.get(client);
  if (!pollers) {
    pollers = new Map();
    pollersByClient.set(client, pollers);
  }
  return pollers;
}

function tick(client: QueryClient, poller: Poller): void {
  const inBackground = [...poller.subs].some(s => s.inBackground);
  if (!inBackground && !focusManager.isFocused()) return;
  const query = client.getQueryCache().find(poller.queryKey, { exact: true });
  if (!query) return;
  const observer = observersOf(query).find(o => o.options.enabled !== false);
  observer?.refetch().catch(() => undefined);
}

/**
 * `Query.fetch()` without options reuses whatever options the LAST fetch set —
 * a `fetchQuery` elsewhere (with its own `retry`, `queryFn`) would otherwise
 * become the poll's options from then on. Refetching through a mounted
 * observer runs the tick with that `useQuery`'s own options, exactly as
 * react-query's per-observer interval does.
 */
function observersOf(
  query: NonNullable<ReturnType<QueryCache['find']>>
): QueryObserver[] {
  return (query as unknown as { observers: QueryObserver[] }).observers ?? [];
}

function reschedule(client: QueryClient, poller: Poller): void {
  const fastest = Math.min(...[...poller.subs].map(s => s.intervalMs));
  if (poller.timer !== undefined && poller.intervalMs === fastest) return;
  if (poller.timer !== undefined) clearInterval(poller.timer);
  poller.intervalMs = fastest;
  poller.timer = setInterval(() => tick(client, poller), fastest);
}

export function subscribeSharedRefetchInterval(
  client: QueryClient,
  queryKey: QueryKey,
  intervalMs: number,
  inBackground = false
): () => void {
  const hash = keyHash(queryKey);
  const pollers = pollersOf(client);
  let poller = pollers.get(hash);
  if (!poller) {
    poller = { queryKey, subs: new Set() };
    pollers.set(hash, poller);
  }
  const sub: Subscription = { intervalMs, inBackground };
  poller.subs.add(sub);
  reschedule(client, poller);

  const owned = poller;
  return () => {
    owned.subs.delete(sub);
    if (owned.subs.size > 0) {
      reschedule(client, owned);
      return;
    }
    if (owned.timer !== undefined) clearInterval(owned.timer);
    if (pollers.get(hash) === owned) pollers.delete(hash);
  };
}

/**
 * Join the shared poll of `queryKey`. Call it next to the `useQuery` that owns
 * the key, with that query's `enabled`, and drop `refetchInterval` from the
 * query itself. `intervalMs: false` (or `enabled: false`) subscribes nothing.
 */
export function useSharedRefetchInterval(
  queryKey: QueryKey,
  intervalMs: number | false,
  opts?: { enabled?: boolean; inBackground?: boolean }
): void {
  const client = useQueryClient();
  const enabled = opts?.enabled ?? true;
  const inBackground = opts?.inBackground ?? false;
  const hash = keyHash(queryKey);

  useEffect(() => {
    if (!enabled || intervalMs === false || intervalMs <= 0) return;
    return subscribeSharedRefetchInterval(
      client,
      queryKey,
      intervalMs,
      inBackground
    );
    // `hash` stands in for `queryKey`, whose identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, hash, intervalMs, enabled, inBackground]);
}
